import { useUrlGenerator } from '@folklore/routes';
import { animated, useTransition } from '@react-spring/web';
import classNames from 'classnames';
import { useEffect, useState } from 'react';

import BrandLogo from '../icons/BrandLogoTimeline';
import BasicButton, { BasicButtonProps } from './BasicButton';

import styles from '../../styles/buttons/brand-logo-button.module.css';

export interface BrandLogoButtonProps extends BasicButtonProps {
    brand: Brand;
    withSponsor?: 'auto' | boolean;
    theme?: 'dark' | 'light';
    sponsorDelay?: number;
    sponsorDuration?: number;
    logoClassName?: string | null;
}

function BrandLogoButton({
    brand,
    withSponsor = false,
    sponsorDelay = 1000,
    sponsorDuration = 3500,
    theme = 'dark',
    className = null,
    logoClassName = null,
    ref: buttonRef = null,
    ...props
}: BrandLogoButtonProps) {
    const url = useUrlGenerator();
    const { slug = null } = brand || {};
    const transitions = useTransition([brand], {
        initial: {
            opacity: 1,
            y: '0%',
        },
        from: {
            opacity: 0,
            y: '-100%',
        },
        enter: {
            opacity: 1,
            y: '0%',
        },
        leave: {
            opacity: 0,
            y: '100%',
        },
        keys: ({ id, handle }) => id || handle,
    });

    const [sponsorVisible, setSponsorVisible] = useState(false);

    useEffect(() => {
        if (withSponsor !== 'auto') {
            return () => {};
        }

        setSponsorVisible(false);
        let timeout = setTimeout(() => {
            setSponsorVisible(true);

            timeout = setTimeout(() => {
                setSponsorVisible(false);
            }, sponsorDuration);
        }, sponsorDelay);

        return () => {
            clearTimeout(timeout);
        };
    }, [brand, withSponsor, sponsorDelay, sponsorDuration]);

    return (
        <BasicButton
            className={classNames([styles.container, className])}
            ref={buttonRef}
            href={
                slug !== null
                    ? url('home', {
                          brand: slug,
                      })
                    : '/'
            }
            {...props}
        >
            {transitions(({ y, ...style }, { handle }, { phase }) => (
                <animated.span
                    className={classNames([styles.logoContainer, styles[phase], styles[handle]])}
                    style={{
                        ...style,
                        y,
                    }}
                >
                    <BrandLogo
                        brand={handle}
                        theme={theme}
                        withSponsor={withSponsor === 'auto' ? sponsorVisible : withSponsor}
                        className={classNames([styles.logo, logoClassName])}
                    />
                </animated.span>
            ))}
        </BasicButton>
    );
}

export default BrandLogoButton;
